import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Leaf, Award, TrendingUp, FileText, Building2, Grid3x3, Droplets, Palette, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import SEO from '../components/SEO';
import { DoYouNeed } from '../components/DoYouNeed';
import { productCategories, solutions, testimonials } from '../mockData';
import { useLanguage } from '../i18n/LanguageContext';

const iconMap = { FileText, Building2, Grid3x3, Droplets, Palette };

const Home = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen">
      <SEO 
        title="Paper Chemicals Supplier in India – Eco-Friendly Wet End Chemicals, Dyes & Paper Mill Solutions"
        description="VB Global is a leading paper chemicals supplier in India (Coimbatore, Tamil Nadu) – eco-friendly wet end chemicals: AKD sizing agent, dry strength resin (DSR), retention & drainage aids, defoamer, burst factor booster, paper dyes & OBA. Also construction chemicals, SS wire mesh and high pressure showers. 29+ years, ISO 9001:2015 certified products."
        keywords="paper chemicals supplier in India, paper chemicals manufacturer India, paper mill chemicals India, wet end chemicals, paper making chemicals, specialty chemicals for paper industry, paper chemicals Coimbatore, paper chemicals Tamil Nadu, AKD sizing agent supplier India, dry strength resin, retention and drainage aid, defoamer paper mill, burst factor booster, cationic starch, paper dyes supplier India, optical brightening agents OBA, construction chemicals Coimbatore, SS wire mesh, high pressure showers, eco-friendly paper chemicals, VB Global"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#8B2C3A] via-[#6B1C2A] to-[#4B0C1A] text-white py-20 md:py-32">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-100 px-4 py-2 rounded-full mb-6 backdrop-blur-sm border border-emerald-400/30">
              <Leaf className="w-4 h-4" />
              <span className="text-sm font-medium">{t('home.heroBadge')}</span>
            </div>
            <h1 data-testid="home-hero-title" className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              {t('home.heroTitle')}
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200">
              {t('home.heroSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/products/paper-chemicals">
                <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B8941F] text-gray-900 font-semibold">
                  {t('home.exploreProducts')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/30 backdrop-blur-sm">
                  {t('home.getInTouch')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <div className="bg-[#8B2C3A] p-3 rounded-full">
                  <Award className="w-6 h-6 text-white" />
                </div>
              </div>
              <h3 className="text-3xl font-bold text-[#8B2C3A] mb-1">29+</h3>
              <p className="text-gray-600">{t('home.yearsExperience')}</p>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <div className="bg-emerald-600 p-3 rounded-full">
                  <Leaf className="w-6 h-6 text-white" />
                </div>
              </div>
              <h3 className="text-3xl font-bold text-emerald-600 mb-1">100%</h3>
              <p className="text-gray-600">{t('home.ecoFriendly')}</p>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <div className="bg-[#8B2C3A] p-3 rounded-full">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
              </div>
              <h3 className="text-3xl font-bold text-[#8B2C3A] mb-1">98%</h3>
              <p className="text-gray-600">{t('home.clientSatisfaction')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Intro: Paper Chemicals Supplier in India */}
      <section className="py-16 bg-white border-b border-gray-100" data-testid="home-seo-intro">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-3">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5">{t('home.seoTitle')}</h2>
              <p className="text-lg text-gray-600 leading-relaxed">{t('home.seoText')}</p>
            </div>
            <div className="lg:col-span-2 bg-gray-50 rounded-2xl p-6 border border-gray-200">
              <div className="flex flex-wrap gap-2 mb-6">
                {[
                  ['AKD Sizing Agent', '/products/paper-chemicals'],
                  ['Dry Strength Resin (DSR)', '/products/paper-chemicals'],
                  ['Retention & Drainage Aid', '/products/paper-chemicals'],
                  ['Anionic Trash Control (Cationic demand)', '/products/paper-chemicals'],
                  ['Defoamer', '/products/paper-chemicals'],
                  ['Burst Factor Booster', '/products/paper-chemicals'],
                  ['Cationic Starch', '/products/paper-chemicals'],
                  ['Paper Dyes & OBA', '/products/dyes'],
                ].map(([label, to]) => (
                  <Link key={label} to={to} className="bg-white border border-gray-200 hover:border-[#8B2C3A] hover:text-[#8B2C3A] text-gray-700 text-sm font-medium px-3 py-1.5 rounded-full transition-colors">
                    {label}
                  </Link>
                ))}
              </div>
              <Link to="/products/paper-chemicals">
                <Button className="w-full bg-[#8B2C3A] hover:bg-[#6B1C2A] text-white" data-testid="home-view-paper-chemicals">
                  {t('home.viewPaperChemicals')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('home.portfolioTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('home.portfolioSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {productCategories.map((category) => {
              const Icon = iconMap[category.icon];
              return (
                <Card key={category.id} data-testid={`home-category-card-${category.slug}`} className="border-2 hover:border-[#8B2C3A] transition-all duration-300 hover:shadow-xl group">
                  <CardHeader>
                    <div className="w-12 h-12 bg-[#8B2C3A] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#6B1C2A] transition-colors">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-xl">{t(`categories.${category.slug}.name`)}</CardTitle>
                    <CardDescription>{t(`categories.${category.slug}.description`)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link to={`/products/${category.slug}`}>
                      <Button variant="outline" className="w-full group-hover:bg-[#8B2C3A] group-hover:text-white group-hover:border-[#8B2C3A] transition-colors">
                        {t('home.learnMore')}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Solutions Preview */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('home.solutionsTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('home.solutionsSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {solutions.slice(0, 3).map((solution) => (
              <Card key={solution.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg text-[#8B2C3A]">{solution.problem}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{solution.solution}</p>
                  <div className="space-y-2">
                    {solution.benefits.slice(0, 2).map((benefit, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Link to="/solutions">
              <Button size="lg" className="bg-[#8B2C3A] hover:bg-[#6B1C2A] text-white">
                {t('home.viewAllSolutions')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Do You Need...? */}
      <DoYouNeed />

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('home.testimonialsTitle')}
            </h2>
            <p className="text-xl text-gray-600">{t('home.testimonialsSubtitle')}</p>
          </div>

          <div className="grid grid-cols-1 max-w-2xl mx-auto gap-6">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2 hover:border-[#D4AF37] transition-colors">
                <CardHeader>
                  <div className="flex gap-1 mb-2">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
                    ))}
                  </div>
                  <CardDescription className="text-gray-700 italic text-base">"{testimonial.text}"</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.position}</p>
                  <p className="text-sm text-[#8B2C3A] font-medium">{testimonial.company}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#8B2C3A] to-[#6B1C2A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {t('home.ctaTitle')}
          </h2>
          <p className="text-xl mb-8 text-gray-200">
            {t('home.ctaSubtitle')}
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B8941F] text-gray-900 font-semibold">
              {t('home.ctaButton')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
